/*
 * Copyright 2026 North Carolina State University
 */

import React from 'react';
import { useBranding } from '../../contexts/BrandingContext';
import phytotronLogo from '../../assets/phytotron-logo.png';

/**
 * The logo and heading at the top of the sign-in and sign-up pages, arranged
 * wherever the admin put them.
 *
 * Both elements are positioned as percentages of this band, and the band keeps
 * a fixed aspect ratio, so the arrangement an admin makes in the branding editor
 * holds at every window size. The editor renders this same component, which is
 * what makes its preview trustworthy: there is no second copy of the layout
 * maths to drift out of step.
 *
 * Below ~700px the stylesheet drops the positioning and stacks the two, because
 * a layout arranged on a desktop rarely survives a phone.
 */
const AuthBrandHeader = ({
  heading,
  layout,
  logoSrc,
  logoAlt,
  subheading,
  // The editor passes these to make the pieces draggable; the real pages don't.
  logoProps = {},
  headingProps = {},
  // The editor swaps the heading text for an editable field, so the admin types
  // it where it will actually appear.
  headingContent,
  children
}) => {
  const { branding, logoUrl, logoAlt: contextAlt } = useBranding();
  const resolved = layout || branding.auth_layout;
  const src = logoSrc !== undefined ? logoSrc : (logoUrl || phytotronLogo);
  const alt = logoAlt !== undefined ? logoAlt : contextAlt;

  const place = ({ x, y, width }) => ({
    left: `${x}%`,
    top: `${y}%`,
    width: `${width}%`
  });

  return (
    <div className="auth-brand-band">
      {src && (
        <img
          {...logoProps}
          src={src}
          alt={alt}
          className={`auth-brand-logo ${logoProps.className || ''}`}
          style={{ ...place(resolved.logo), ...(logoProps.style || {}) }}
        />
      )}

      <h1
        {...headingProps}
        className={`auth-brand-heading ${headingProps.className || ''}`}
        style={{
          ...place(resolved.heading),
          textAlign: resolved.heading_align,
          // Sized against the band's width so the text scales with the layout.
          // The px value is a fallback for browsers without container units.
          fontSize: `${resolved.heading_size * 12}px`,
          ...(headingProps.style || {})
        }}
      >
        {headingContent || (
          <span style={{ fontSize: `${resolved.heading_size}cqw` }}>{heading}</span>
        )}
      </h1>

      {subheading && <p className="auth-brand-subheading">{subheading}</p>}
      {children}
    </div>
  );
};

export default AuthBrandHeader;
